import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import SearchModal from './SearchModal';
import ThemeToggle from './ThemeToggle';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import * as FiIcons from 'react-icons/fi';

const { FiMenu, FiX, FiSearch, FiShoppingBag } = FiIcons;

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const location = useLocation();
  const { openCart, cartCount } = useCart();
  const { user } = useAuth();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsMenuOpen(false);
  }, [location.pathname]);

  const navItems = [
    { name: 'About Tom', path: '/about' },
    { name: 'Mentorship', path: '/mentorship' },
    { name: 'Success Stories', path: '/success' },
    { name: 'Shop', path: '/shop' },
    { name: 'Book', path: '/book' },
    { name: 'FAQ', path: '/faq' },
    { name: 'Contact', path: '/contact' }
  ];

  const isActive = (path) => location.pathname.startsWith(path);

  // Always solid (dark/static) to ensure consistency across all pages and themes
  const isSolid = true;

  return (
    <>
      <header
        className={`fixed top-0 w-full z-50 transition-all duration-300 ${isSolid
          ? 'bg-slate-900/95 backdrop-blur-md shadow-sm py-4'
          : 'bg-transparent py-5'
          }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center">
            <Link
              to="/"
              className="flex items-center space-x-3 group"
              onClick={() => setIsMenuOpen(false)}
            >
              <div className="w-12 h-12 rounded-full overflow-hidden transition-transform duration-300 hover:scale-110 bg-white flex items-center justify-center">
                <span className="text-slate-900 font-serif font-bold text-lg">TM</span>
              </div>
              <span
                className="font-serif font-bold text-xl tracking-tight transition-colors duration-300 text-white"
              >
                Tom McKay
              </span>
            </Link>

            <nav className="hidden md:flex items-center space-x-4">
              {navItems.map((item) => (
                <Link
                  key={item.name}
                  to={item.path}
                  className={`text-sm font-medium transition-all duration-300 px-2 text-white/80 hover:text-white ${isActive(item.path) ? 'text-white font-bold' : ''}`}
                >
                  {item.name}
                </Link>
              ))}

              <ThemeToggle />

              <button
                onClick={openCart}
                className="p-2 rounded-lg transition-colors relative text-white/80 hover:bg-white/20"
              >
                <SafeIcon icon={FiShoppingBag} className="w-5 h-5" />
                {cartCount > 0 && (
                  <span className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 text-white text-[10px] flex items-center justify-center rounded-full font-bold">
                    {cartCount}
                  </span>
                )}
              </button>

              <button
                onClick={() => setIsSearchOpen(true)}
                className="p-2 rounded-lg transition-colors text-white/80 hover:bg-white/20"
              >
                <SafeIcon icon={FiSearch} className="w-5 h-5" />
              </button>

              <Link
                to="/onboarding"
                className="px-6 py-2.5 rounded-full text-sm font-bold transition-all duration-300 hover:scale-105 bg-emerald-500 text-white hover:bg-emerald-600 shadow-lg"
              >
                Apply Now
              </Link>
            </nav>

            <div className="md:hidden flex items-center space-x-2">
              <button
                onClick={openCart}
                className="p-2 rounded-lg transition-colors relative text-white"
              >
                <SafeIcon icon={FiShoppingBag} className="w-6 h-6" />
                {cartCount > 0 && (
                  <span className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 text-white text-[10px] flex items-center justify-center rounded-full font-bold">
                    {cartCount}
                  </span>
                )}
              </button>
              <ThemeToggle />
              <button
                className="p-2 rounded-lg transition-colors text-white"
                onClick={() => setIsMenuOpen(!isMenuOpen)}
              >
                <SafeIcon icon={isMenuOpen ? FiX : FiMenu} className="w-6 h-6" />
              </button>
            </div>
          </div>
        </div>

        <AnimatePresence>
          {isMenuOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="md:hidden bg-slate-900 border-t border-slate-800 overflow-hidden"
            >
              <div className="px-4 py-6 space-y-4 flex flex-col items-center">
                {navItems.map((item) => (
                  <Link
                    key={item.name}
                    to={item.path}
                    className="text-lg font-serif font-medium text-slate-200 py-2"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    {item.name}
                  </Link>
                ))}
                <div className="w-full h-px bg-slate-800 my-4" />
                <Link
                  to="/onboarding"
                  className="w-full text-center bg-emerald-500 text-white px-6 py-4 rounded-xl font-bold text-lg"
                  onClick={() => setIsMenuOpen(false)}
                >
                  Apply for Mentorship
                </Link>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </header>

      <SearchModal isOpen={isSearchOpen} onClose={() => setIsSearchOpen(false)} />
    </>
  );
};

export default Header;